/**
 * LocaleContext — session-scoped locale / timezone / currency preferences.
 *
 * Source of truth (in priority order):
 *   1. Explicit user override stored in localStorage (``lenavio.locale``
 *      / ``lenavio.timezone`` / ``lenavio.currency``). Written by the
 *      language switcher; read on boot so refreshes survive. Legacy
 *      ``peopleos.*`` keys are migrated once in ``i18n/index.ts``.
 *   2. The browser's ``navigator.language`` and
 *      ``Intl.DateTimeFormat().resolvedOptions().timeZone`` as a sensible
 *      first-run default.
 *
 * We intentionally don't block render on fetching the server profile here
 * — AuthContext does that separately, and once it lands a caller can push
 * the org/employee preference into this context via ``setLocale`` etc.
 *
 * Exposes ``useFormat()`` so callers can do ``format.date(x)`` without
 * threading locale/timezone/currency through every prop.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import i18n, {
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@/i18n";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatTime,
} from "@/lib/format";

const TIMEZONE_STORAGE_KEY = "lenavio.timezone";
const CURRENCY_STORAGE_KEY = "lenavio.currency";

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function normalizeLocale(raw: string | null | undefined): SupportedLocale {
  if (!raw) return "en-US";
  const codes = SUPPORTED_LOCALES.map((l) => l.code);
  if ((codes as readonly string[]).includes(raw)) return raw as SupportedLocale;
  // "pt" → "pt-BR", "en" → "en-US". Fall through to en-US for unknowns.
  const prefix = raw.split("-")[0];
  const match = codes.find((c) => c.toLowerCase().startsWith(prefix.toLowerCase()));
  return (match as SupportedLocale) ?? "en-US";
}

interface LocaleState {
  locale: SupportedLocale;
  timezone: string;
  currency: string;
  setLocale: (l: SupportedLocale) => void;
  setTimezone: (tz: string) => void;
  setCurrency: (c: string) => void;
}

export const LocaleContext = createContext<LocaleState | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    normalizeLocale(
      // Default to English. We intentionally skip ``navigator.language``
      // here so a Spanish-locale browser boots in English; the user can
      // switch via the language switcher, which writes to localStorage.
      typeof window !== "undefined"
        ? localStorage.getItem(LOCALE_STORAGE_KEY) || "en-US"
        : "en-US"
    )
  );
  const [timezone, setTimezoneState] = useState<string>(() =>
    (typeof window !== "undefined" &&
      localStorage.getItem(TIMEZONE_STORAGE_KEY)) ||
    detectTimezone()
  );
  const [currency, setCurrencyState] = useState<string>(() =>
    (typeof window !== "undefined" &&
      localStorage.getItem(CURRENCY_STORAGE_KEY)) ||
    "USD"
  );

  // Keep i18next in lockstep with our locale state so ``t()`` updates
  // immediately when the user toggles the language switcher.
  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [locale]);

  const setLocale = useCallback((l: SupportedLocale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, l);
    } catch {
      // ignore — private-mode Safari, etc.
    }
  }, []);

  const setTimezone = useCallback((tz: string) => {
    setTimezoneState(tz);
    try {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, tz);
    } catch {
      // ignore
    }
  }, []);

  const setCurrency = useCallback((c: string) => {
    setCurrencyState(c);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, c);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<LocaleState>(
    () => ({ locale, timezone, currency, setLocale, setTimezone, setCurrency }),
    [locale, timezone, currency, setLocale, setTimezone, setCurrency]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleState {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used inside LocaleProvider");
  return ctx;
}

/**
 * Pre-bound formatters — the 90% of UI code that just wants to render a
 * date/number/currency and doesn't care about the underlying locale.
 */
export function useFormat() {
  const { locale, timezone, currency } = useLocale();
  return useMemo(
    () => ({
      date: (input: Parameters<typeof formatDate>[0], opts?: Intl.DateTimeFormatOptions) =>
        formatDate(input, { locale, timeZone: timezone }, opts),
      dateTime: (
        input: Parameters<typeof formatDateTime>[0],
        opts?: Intl.DateTimeFormatOptions
      ) => formatDateTime(input, { locale, timeZone: timezone }, opts),
      time: (
        input: Parameters<typeof formatTime>[0],
        opts?: Intl.DateTimeFormatOptions
      ) => formatTime(input, { locale, timeZone: timezone }, opts),
      number: (
        value: Parameters<typeof formatNumber>[0],
        opts?: Intl.NumberFormatOptions
      ) => formatNumber(value, { locale }, opts),
      percent: (
        value: Parameters<typeof formatPercent>[0],
        opts?: Intl.NumberFormatOptions
      ) => formatPercent(value, { locale }, opts),
      currency: (
        value: Parameters<typeof formatCurrency>[0],
        overrideCurrency?: string,
        opts?: Intl.NumberFormatOptions
      ) =>
        formatCurrency(
          value,
          { locale, currency: overrideCurrency || currency },
          opts
        ),
    }),
    [locale, timezone, currency]
  );
}
